import Image from "next/image";
import useRouterStuff from "@hooks/useRouterStuff";
import { Prisma } from "@prisma/client";
import { getAPIClient } from "@services/axios";
import { getMonths } from "@shared/constants";
import { convertTimezoneBasedOnDate, getTimezonesData } from "@shared/utils";
import { useTranslation } from "react-i18next";

import styles from "./style.module.scss";

const REQUIRED_REVIEW = "REQUIRED_REVIEW";
const REJECTED = "REJECTED";
const ACCEPTED = "ACCEPTED";

export default function EventListComponent({
  showMonthHeader,
  month,
  eventYear,
  startDate,
  endDate,
  differentDays,
  event,
  eventTimeLabel,
  currentSubTabCalendar,
  isSiteAdminPage,
  editEvents,
  moderation,
  screenSize,
  usersTimezoneData,
  timezonesData,
  setIsOpen,
  setSelectedEvent,
}: {
  showMonthHeader: boolean;
  month: { name: string; number: string; short: unknown };
  eventYear: string;
  startDate: string;
  endDate: string;
  differentDays: boolean;
  event: Prisma.EventGetPayload<{
    include: {
      Partner: true;
      LocatorCompany: true;
      EventTypes: true;
      region: true;
    };
  }>;
  eventTimeLabel: string;
  currentSubTabCalendar: string;
  isSiteAdminPage: boolean;
  editEvents: boolean;
  moderation: boolean;
  screenSize: {
    width: number;
    height: number;
  };
  usersTimezoneData: { currentTimeOffsetInMinutes: number; label: string };
  timezonesData: ReturnType<typeof getTimezonesData>;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedEvent: React.Dispatch<
    React.SetStateAction<Record<string, unknown>>
  >;
}) {
  const getEventImage = async (event) => {
    const res = await getAPIClient().get<{ image: string }>(
      `/events/image/${event.id}`
    );
    return res.data.image;
  };

  const { t } = useTranslation();
  const months = getMonths(t);
  const { queryParams } = useRouterStuff();

  const calendarImage =
    event.contentStatus === REQUIRED_REVIEW
      ? "/images/calendar_events_yellow.svg"
      : event.contentStatus === REJECTED
        ? "/images/calendar_events_red.svg"
        : "/images/calendar_events.svg";

  const startDay = startDate.split("T")[0].split("-")[2];
  const endDay = endDate.split("T")[0].split("-")[2];
  const endMonthNumber = endDate.split("T")[0].split("-")[1];
  const endYear = endDate.split("T")[0].split("-")[0];

  const eventMonthShort = months?.find(
    (el) => el.number === endMonthNumber
  )?.short;

  const formattedDateRange = `${startDay} ${
    differentDays ? ` - ${endDay}` : ""
  } ${eventMonthShort}, ${endYear}`;

  const formattedEventTime = `${eventTimeLabel} ${convertTimezoneBasedOnDate(
    usersTimezoneData.label,
    startDate,
    timezonesData,
    true
  )}`;

  const truncatedTagline =
    event.tagline.length > 15 && screenSize.width <= 768
      ? `${event.tagline.slice(0, 15)}...`
      : event.tagline;

  const getButtonLabel = () => {
    if (moderation || event.contentStatus !== ACCEPTED) {
      return `${t("moderation")} >`;
    } else if (
      editEvents ||
      (isSiteAdminPage &&
        currentSubTabCalendar !== "pe" &&
        currentSubTabCalendar !== "vcsp")
    ) {
      return `${t("edit_event")} >`;
    } else {
      return `${t("view_event")} >`;
    }
  };

  const handleEventClick = async () => {
    const evImg = await getEventImage(event);
    queryParams({
      set: {
        eventId: event.id.toString(),
      },
    });
    setSelectedEvent(() => {
      return {
        ...event,
        image: evImg,
        locatorCompanyId: event?.LocatorCompany?.id,
        regionId: event.region,
        type: event.EventTypes,
      };
    });
  };

  return (
    <>
      {showMonthHeader ? (
        <>
          <h1>
            {month.name} {eventYear}
          </h1>

          <div className={styles.line} />
        </>
      ) : null}
      <div className={styles.event_wrapper} key={`event_${event.id}`}>
        <div className={styles.event_info}>
          <div className={styles.event_date_wrapper}>
            <Image
              alt="calendar"
              className={styles.calendar_img}
              height={screenSize.width < 590 ? 20 : 30}
              src={calendarImage}
              width={screenSize.width < 590 ? 20 : 30}
            />
            <div className={styles.event_date}>
              <span className={styles.date}>{formattedDateRange}</span>
              <span className={styles.time}>{formattedEventTime}</span>
            </div>
          </div>
          {event?.Partner?.name && event?.Partner?.name?.length > 0 ? (
            <div className={styles.partner_wrapper}>
              <span>{t("Partner")}</span>
              <span className={styles.partner_name}>{event.Partner.name}</span>
            </div>
          ) : null}
          {isSiteAdminPage &&
          event?.LocatorCompany?.name &&
          event?.LocatorCompany?.name?.length > 0 ? (
            <div className={styles.partner_wrapper}>
              <span>{t("provider")}</span>
              <span className={styles.partner_name}>
                {event.LocatorCompany.name}
              </span>
            </div>
          ) : null}
          <div className={styles.event_title}>
            <span>{event.title}</span>
            <span className={styles.tagline}>{truncatedTagline}</span>
          </div>
        </div>
        <div className={styles.event_button}>
          {moderation || event.contentStatus !== ACCEPTED ? (
            event.contentStatus === REQUIRED_REVIEW ? (
              <span className={styles.pending}>({t("pending")})</span>
            ) : (
              <span className={styles.change}>({t("required_change")})</span>
            )
          ) : null}
          <span className={styles.btn} onClick={handleEventClick}>
            {getButtonLabel()}
          </span>
        </div>
      </div>
    </>
  );
}
